// The Trail screen (shared/trail.ts), opened from the world map and the app bar: one path per region, trainer nodes
// in order and the region boss at the end. #/trail/:region/:node picks a node. A trainer node shows who they are,
// their line-up, the first-clear reward and the fight; a boss node is the old Bosses screen for that boss (its card,
// estimate, last result and the Boss Ladder), plus its first-clear reward. Fights run on the server and come back as
// { t: 'trail', fight }; the last one replays here from its seed (client/arena.ts battleLog draws it).
import { psecTabs, psecWrap, phoneSections } from './psec.ts';
import { TRAIL, TRAIL_NODE, TRAIL_LEVEL, IDEA_NAME, bossNode, bossProblem, cleared, nextNode, pathOf, pathProgress, trailFight, trailProblem, trailTeam, type TrailBoss, type TrailFightDTO, type TrailNode, type TrailReward, type TrailState, type TrailTrainer } from '../shared/trail.ts';
import { HELD_ITEMS, heldName } from '../shared/held.ts';
import { heldRange } from './held.ts';
import { BOSSES, SPECIES } from '../shared/species.ts';
import { REGIONS, GROUND } from '../shared/grounds.ts';
import { BOSS_TOKEN, ITEM } from '../shared/items.ts';
import { CONFIG } from '../shared/config.ts';
import { canFight } from '../shared/xp.ts';
import type { BattleResult } from '../shared/arena.ts';
import type { ClientMsg, CreatureDTO } from '../shared/protocol.ts';
import { S } from './state.ts';
import { iconUrl } from './assets.ts';
import { esc, full, cname } from './fmt.ts';
import { playerIcon } from './playericon.ts';
import { ICON } from './icons.ts';
import { teamFace, battleLog } from './arena.ts';
import { heldBadge } from './held.ts';
import { synergyChips } from './synergy.ts';
import { bossCardHtml, bossResultHtml } from './pages.ts';
import { bossBattleOf } from './bossboard.ts';
import { ladderSection, pieceDrop } from './ladder.ts';
import { go } from './router.ts';
import { BOARD, type BoardRow } from '../shared/board.ts';
import { asFighter } from '../shared/social.ts';
import { boardOn, frontLabel, pickFrom, reachBadge, rowClick, rowToggle, rowsOut, setRow, shownRows, type RowPick } from './rows.ts';
import { afterReplay, boardChips, boardPrep, boardView, replayFromStart, replayOver } from './boardview.ts';
import { teamStrip } from './teampick.ts';
import { openTeamBuilder } from './teambuilder.ts';
import { mySprite, trainerSprite } from './trainerart.ts';
import { rowsShown } from '../shared/pvpfight.ts';
import { SYNERGY_SWITCH } from '../shared/synergy.ts';

let send: (m: ClientMsg) => void = () => {};
let rerender: () => void = () => {};
export function bindTrail(o: { send: (m: ClientMsg) => void; render: () => void }) { send = o.send; rerender = o.render; }

/**
 * The region and node on screen (from the route), whether the last fight's blow log is open, and your team's rows as
 * set here (the board; null: the rows of your last Trail fight, else defaultRows).
 */
const T = { region: 'midgard', node: '', watch: false, rows: null as RowPick, view: 'board' as 'board' | 'fight', ids: null as number[] | null };

const coin = (n: number) => `<span class="coinv"><img src="${iconUrl('coin')}" alt="coins">${full(n)}</span>`;
const bossOf = (n: TrailBoss) => BOSSES.find((b) => b.id === n.boss)!;

/** Set the screen from #/trail/:region/:node (an unknown part falls back to the trainer's next stop). */
export function openTrailRoute(region: string, node: string) {
  const t = S.trainer;
  const home = GROUND[t?.ground ?? '']?.region ?? 'midgard';
  T.region = pathOf(region) ? region : pathOf(home) ? home : TRAIL[0].region;
  const n = TRAIL_NODE[node];
  T.node = n && n.region === T.region ? n.id : nextNode(T.region, t?.trail)?.id ?? '';
  if (S.trailFight?.node !== T.node) { T.watch = false; T.view = 'board'; }
}

/**
 * Where #/bosses now leads: the boss node of the last boss fought, else of the highest boss whose key is in the bag,
 * else of the region being hunted.
 */
export function bossesRedirect(): string {
  const t = S.trainer;
  const keyed = [...BOSSES].reverse().find((b) => (t?.bag[BOSS_TOKEN[b.id]] ?? 0) > 0);
  const boss = S.boss?.boss ?? S.ladder?.boss ?? keyed?.id ?? BOSSES.find((b) => b.region === (GROUND[t?.ground ?? '']?.region ?? 'midgard'))?.id ?? BOSSES[0].id;
  const n = bossNode(boss);
  return n ? `#/trail/${n.region}/${n.id}` : '#/trail';
}

/** True while this boss's Trail stop is on screen, where its fight plays on the board with the result under it. */
export function trailBossShown(boss: string): boolean {
  const n = TRAIL_NODE[T.node];
  return S.tab === 'trail' && n?.kind === 'boss' && n.boss === boss;
}

/** A first clear whose fight is still playing out: shown as not yet cleared until the replay ends or Skip. */
function pendingClear(): string | null {
  const f = S.trailFight;
  if (f?.win && f.first && T.view === 'fight' && f.node === T.node && f.result && !replayOver(f.result)) return f.node;
  const b = S.boss, fc = b?.firstClear, r = b && bossBattleOf(b);
  if (fc && r && !replayOver(r)) return fc.node;
  return null;
}
/** Your Trail progress as it should look right now (see pendingClear). */
function shownTrail(): TrailState | undefined {
  const tr = S.trainer?.trail, id = pendingClear();
  if (!tr || !id || !(id in tr.cleared)) return tr;
  const { [id]: _, ...rest } = tr.cleared;
  return { ...tr, cleared: rest };
}

/** The world map's way onto a region's Trail, once the Trail is open: "Trail 3/7". */
export function trailMapLink(region: string): string {
  const t = S.trainer!;
  if (!S.trailOn || t.level < TRAIL_LEVEL || !pathOf(region)) return '';
  const pr = pathProgress(region, shownTrail());
  return `<a class="btn small tl-maplink" href="#/trail/${region}">Trail <span class="tl-n">${pr.done}/${pr.of}</span></a>`;
}

export function trailHead() {
  const t = S.trainer!;
  const done = TRAIL.reduce((a, p) => a + pathProgress(p.region, shownTrail()).done, 0);
  const all = TRAIL.reduce((a, p) => a + p.nodes.length, 0);
  return { meta: `${done} of ${all} cleared`, actions: '<a class="link" href="#/help/trail">How the Trail works</a>' };
}

export function trailPane(): string {
  const t = S.trainer!;
  if (!S.trailOn) return '<p class="hint">The Trail is closed for now.</p>';
  const p = pathOf(T.region)!;
  const node = TRAIL_NODE[T.node] ?? p.nodes[0];
  // The detail first: it starts the fight's replay, which decides whether a fresh clear shows yet.
  const detail = t.level < TRAIL_LEVEL ? '' : node.kind === 'trainer' ? trainerDetail(node) : bossDetail(node);
  const tabs = `<div class="seg tl-regions" role="group" aria-label="Region">${TRAIL.map((x) => {
    const pr = pathProgress(x.region, shownTrail()), boss = x.nodes.find((n): n is TrailBoss => n.kind === 'boss');
    const rg = REGIONS.find((r) => r.id === x.region);
    return `<button class="${x.region === T.region ? 'on' : ''}" data-act="trail-region" data-id="${x.region}" aria-pressed="${x.region === T.region}">${boss ? `<img class="tl-emblem" src="${iconUrl(`${boss.boss}-crest`)}" alt="">` : ''}<span>${esc(rg?.name ?? x.region)}</span><span class="tl-n">${pr.done}/${pr.of}</span></button>`;
  }).join('')}</div>`;
  if (t.level < TRAIL_LEVEL) return `${tabs}<section class="panel tl-locked"><h3>Opens at trainer Lv ${TRAIL_LEVEL}</h3></section>`;
  if (centred !== node.id) { centred = node.id; requestAnimationFrame(centreStop); }
  return `${tabs}<div class="tl-wrap"><ol class="panel tl-path" aria-label="${esc(p.name)}">${p.nodes.map((n, i) => stop(n, i, node.id)).join('')}</ol>
    <div class="tl-detail">${detail}</div></div>`;
}

/** On a phone the path is a sideways strip: bring the chosen stop into view once each time the choice changes. */
let centred = '';
function centreStop() {
  const el = document.querySelector<HTMLElement>('.tl-stop.on'), strip = el?.parentElement;
  if (!el || !strip || strip.scrollWidth <= strip.clientWidth) return;
  strip.scrollLeft += el.getBoundingClientRect().left - strip.getBoundingClientRect().left - (strip.clientWidth - el.offsetWidth) / 2;
}

/** One stop on the path: its face, name and state (cleared, open, or the level it opens at). */
function stop(n: TrailNode, i: number, sel: string): string {
  const t = S.trainer!;
  const done = cleared(shownTrail(), n.id);
  const why = n.kind === 'trainer' ? trailProblem(n, t.level, shownTrail()) : bossProblem(n.boss, shownTrail());
  const lockedLv = n.kind === 'trainer' && t.level < n.level;
  const state = done ? 'done' : why ? 'shut' : 'open';
  const face = n.kind === 'trainer'
    ? playerIcon({ outfit: n.outfit, comp: { species: n.team[0].s }, size: 'sm' })
    : `<img class="tl-bosspic" src="${iconUrl(`${n.boss}-crest`)}" alt="">`;
  const name = n.kind === 'trainer' ? n.name : `${SPECIES[n.boss]?.name ?? n.boss}`;
  const sub = done ? '<span class="tl-ok">Cleared</span>'
    : n.kind === 'boss' ? `${why ? ICON.lock : ''}<span>Boss · Lv ${full(n.level)}</span>`
    : lockedLv ? `${ICON.lock}<span>Lv ${full(n.level)}</span>` : why ? `${ICON.lock}<span>${esc(IDEA_NAME[n.idea])}</span>` : esc(IDEA_NAME[n.idea]);
  return `<li class="tl-stop ${state}${n.kind === 'boss' ? ' boss' : ''}${n.id === sel ? ' on' : ''}"><button data-act="trail-node" data-id="${n.id}" aria-current="${n.id === sel ? 'step' : 'false'}">
    <span class="tl-dot">${i + 1}</span><span class="tl-face">${face}</span><span class="tl-txt"><b>${esc(name)}</b><span class="tl-sub">${sub}</span></span></button></li>`;
}

const rewardHtml = (r: TrailReward) => `<div class="rewards">${coin(r.coins)}${r.items.map((x) => `<span class="rw"><img src="${iconUrl(x.item)}" alt="">${esc(ITEM[x.item]?.name ?? x.item)} ×${full(x.qty)}</span>`).join('')}</div>`;

/** Who may fight on the Trail: yours, not listed, and not more than LEVEL_SLACK levels above you (server/trail.ts). */
const mayFight = (c: CreatureDTO | undefined): c is CreatureDTO => !!c && !c.listed && canFight(c, S.trainer!.level);

/** The Trail line-up's ids: as set here, else your last Trail fight's, else your hunt team. */
function lineIds(): number[] {
  const t = S.trainer!;
  if (T.ids) return T.ids;
  const last = t.trail?.last?.team.map((c) => c.id).filter((id) => mayFight(S.creatures.get(id)));
  return (last?.length ? last : t.team).slice(0, CONFIG.teamSize);
}

function myTeam(): CreatureDTO[] {
  return lineIds().map((id) => S.creatures.get(id)).filter(mayFight);
}

/** Your team's rows on the Trail, by id: set here, else as your last Trail fight stood, else defaultRows. */
function myRows(team: CreatureDTO[]): Map<number, BoardRow> {
  const last = S.trainer?.trail?.last;
  const pick = T.rows ?? (last?.rows ? pickFrom(last.team.map((c) => c.id), last.rows) : null);
  const line = team.map(asFighter);
  const rows = shownRows(line, pick);
  return new Map(line.map((c, k) => [c.id, rows[k]]));
}

function trainerDetail(n: TrailTrainer): string {
  const t = S.trainer!;
  const done = cleared(t.trail, n.id);
  const why = trailProblem(n, t.level, t.trail);
  const team = myTeam();
  const rows = myRows(team);
  const head = `<div class="tl-who">${playerIcon({ outfit: n.outfit, comp: { species: n.team[0].s }, size: 'lg', name: n.name })}
      <div class="grow"><h3>${esc(n.name)}</h3><div class="chips"><span class="chip">${esc(IDEA_NAME[n.idea])}</span><span class="chip">Trainer Lv ${full(n.level)}</span>${done ? '<span class="chip good">Cleared</span>' : ''}</div>
      <p class="tl-line">“${esc(n.line)}”</p></div></div>`;
  const reward = `<h4 class="sec">${done ? 'First clear, paid' : 'First clear'}</h4>${rewardHtml(n.reward)}`;
  const go = `<button class="btn ${done ? '' : 'primary'}" data-act="trail-fight" data-id="${n.id}"${why || !team.length ? ' disabled' : ''}>${done ? 'Rematch' : `Challenge ${esc(n.name.split(' ')[0])}`}</button>`;
  if (!boardOn()) {
    const theirs = trailTeam(n).map((c, i) => teamFace({ species: c.species, level: c.level }, `<i>${i + 1}</i>`)).join('');
    const mine = team.length ? `<div class="ar-team">${team.map((c, i) => teamFace(c, `<i>${i + 1}</i>${heldBadge(c)}`)).join('')}</div>` : '<div class="ar-team"><span class="muted">Your team is empty.</span></div>';
    return `<section class="panel tl-card">${head}
    <h4 class="sec">Their team</h4><div class="ar-team">${theirs}</div>${synergyChips(trailTeam(n))}
    <h4 class="sec">Your team</h4>${mine}${synergyChips(team)}${reward}
    <div class="lentry"><span class="muted">${esc(why)}</span><span class="spacer"></span>${go}</div></section>${fightHtml(n)}`;
  }
  // The board: set up here, then watch the fight on the same board.
  const f = lastFight(n);
  if (T.view === 'fight' && f) {
    const r = f.result();
    const board = boardView(r, 0, ['You', n.name], 'plain', { scene: n.region, trainers: [mySprite(), trainerSprite(n.outfit)] });
    // What the fight decides (a fresh clear, its reward, the way on) waits for the replay's end or Skip.
    const fresh = !!(f.fresh?.win && f.fresh.first);
    const paid = fresh && f.fresh!.reward ? `<h4 class="sec">First clear</h4>${rewardHtml(f.fresh!.reward)}` : '';
    const cleared = '<span class="chip good">Cleared</span>';
    const top = fresh ? head.replace(cleared, afterReplay(r, cleared, 'span')) : head;
    const most = Math.max(...r.sides.map((x) => x.team.length), 1);
    return `<section class="panel tl-card tl-fight" style="--bv-n:${most}">${top}
      <div class="tl-board">${board}${boardChips(r, 0, ['You', n.name])}</div>${afterReplay(r, paid)}
      <div class="lentry">${resultButtons(n, f.win, !!(why || !team.length), r)}</div></section>`;
  }
  const foes = trailTeam(n);
  const foeRows = rowsShown(foes, n.rows ?? null, SYNERGY_SWITCH.on ? {} : null);
  const board = team.length ? boardPrep({
    key: `trail:${n.id}`,
    mine: team.map((c) => ({ id: c.id, species: c.species, level: c.level, aura: c.rolls.aura, row: rows.get(c.id) ?? 'back', held: c.held })),
    theirs: foes.map((c, i) => ({ id: c.id, species: c.species, level: c.level, row: foeRows[i] })),
    labels: ['You', n.name],
    scene: n.region,
    trainers: [mySprite(), trainerSprite(n.outfit)],
    onRows: (picked) => { T.rows = picked; rerender(); },
  }) : '<div class="ar-team"><span class="muted">Your team is empty.</span></div>';
  const t2 = S.trainer!;
  const hunt = t2.team.filter((id) => mayFight(S.creatures.get(id))).slice(0, CONFIG.teamSize);
  const isHunt = hunt.length === team.length && hunt.every((id, i) => team[i].id === id);
  // A phone shows the board or the team and reward, one at a time (client/psec.ts); the fight button stays under both.
  const keys = ['duel', 'team'];
  return `<section class="panel tl-card">${psecTabs('trail', [{ key: 'duel', title: 'Duel' }, { key: 'team', title: 'Team and reward' }])}
    ${psecWrap('trail', 'duel', keys, `${head}<div class="tl-board">${board}</div>`)}
    ${psecWrap('trail', 'team', keys, `<div class="tn-sec"><h4>Team</h4><span class="spacer"></span>${isHunt ? '' : '<button class="btn small" data-act="trail-hunt">Use hunt team</button>'}<button class="btn small" data-act="trail-team">Change team</button></div>
    ${teamStrip(team.map(asFighter), CONFIG.teamSize, 'trail-team')}
    <div class="tl-syn"><div><b>You</b>${synergyChips(team)}</div><div><b>${esc(n.name.split(' ')[0])}</b>${synergyChips(foes)}</div></div>
    ${reward}`)}
    <div class="lentry"><span class="muted">${esc(why)}</span><span class="spacer"></span>${f ? '<button class="btn" data-act="trail-watch">Replay</button>' : ''}${go}</div>
  </section>`;
}

/** After a fight: Done (back to this trainer), Fight again, and after a win the next trainer on the path. */
function resultButtons(n: TrailTrainer, win: boolean, shut: boolean, r: BattleResult): string {
  const next = win ? nextNode(n.region, S.trainer!.trail) : undefined;
  const ahead = next && next.id !== n.id ? next : undefined;
  const again = `<button class="btn${win ? '' : ' primary'}" data-act="trail-fight" data-id="${n.id}"${shut ? ' disabled' : ''}>Fight again</button>`;
  const on = ahead ? `<button class="btn primary" data-act="trail-node" data-id="${ahead.id}">Next: ${esc(ahead.kind === 'trainer' ? ahead.name.split(' ')[0] : SPECIES[ahead.boss]?.name ?? 'Boss')}</button>` : '';
  return `<button class="btn${win && !ahead ? ' primary' : ''}" data-act="trail-board">Done</button><span class="spacer"></span>${again}${afterReplay(r, on, 'span')}`;
}

/** The last fight at this node: the server's answer this session, or the trainer's saved one replayed from its seed. */
function lastFight(n: TrailTrainer): { win: boolean; fresh: TrailFightDTO | null; result: () => BattleResult } | null {
  const t = S.trainer!;
  const f: TrailFightDTO | null = S.trailFight?.node === n.id ? S.trailFight : null;
  const last = !f && t.trail?.last?.node === n.id ? t.trail.last : null;
  if (!f && !last) return null;
  return { win: f ? f.win : last!.win, fresh: f, result: () => (f ? f.result : trailFight(n, last!.team, last!.seed, t.name, last!.rows, last!.board)) };
}

/** The last fight at this node with the board off: a result panel with its blow log. */
function fightHtml(n: TrailTrainer): string {
  const f = lastFight(n);
  if (!f) return '';
  return `<section class="panel tl-res ${f.win ? 'win' : 'loss'}"><div class="row"><h3>${f.win ? `${esc(n.name)} beaten` : `${esc(n.name)} won`}</h3><span class="spacer"></span>
      <button class="btn small" data-act="trail-watch" aria-expanded="${T.watch}">${T.watch ? 'Hide the fight' : 'Watch the fight'}</button></div>
    ${f.fresh?.win && f.fresh.first && f.fresh.reward ? `<p class="tl-paid muted">First clear</p>${rewardHtml(f.fresh.reward)}` : ''}
    ${T.watch ? battleLog(f.result(), 0) : ''}</section>`;
}

function bossDetail(n: TrailBoss): string {
  const t = S.trainer!;
  const bd = bossOf(n);
  const res = bossResultHtml(n.boss);
  const done = cleared(shownTrail(), n.id);
  const pool = HELD_ITEMS.filter((d) => d.boss === n.boss);
  const piece = pieceDrop(n.boss, 'boss');
  const first = `<section class="panel tl-first${done ? ' done' : ''}"><h4>${done ? 'First clear, paid' : 'First clear'}</h4>
    <div class="rewards"><span class="rw"><img src="${iconUrl('diamond')}" alt="">${CONFIG.trail.bossDiamonds} diamonds</span><span class="rw">One held item, quality 50-100%</span></div>
    <ul class="tl-pool">${pool.map((d) => `<li><img src="${iconUrl(d.id)}" alt=""><b>${esc(d.name)}</b><span class="muted">${esc(heldRange(d.id))}</span></li>`).join('')}</ul></section>`;
  // The boss, its first-clear prize and its ladder take turns under tabs at every size, so the stop never scrolls.
  return `<div class="tl-boss">${phoneSections('tlboss', [
    { key: 'boss', title: SPECIES[n.boss]?.name ?? 'Boss', html: `${res}${bossCardHtml(bd, myTeam(), piece)}` },
    { key: 'first', title: done ? 'First clear, paid' : 'First clear', html: `${first}<p class="tl-links"><a class="link" href="#/bag/craft">Crafting bench</a> · <a class="link" href="#/help/bosses">How bosses work</a> · <a class="link" href="#/help/ladder">How the ladder works</a></p>` },
    { key: 'ladder', title: 'Boss Ladder', html: ladderSection(n.boss) },
  ], undefined, true)}</div>`;
}

/** Trail clicks (data-act="trail-…"); true when handled. */
export function trailClick(el: HTMLElement): boolean {
  const id = el.dataset.id ?? '';
  const act = el.dataset.act ?? '';
  switch (act) {
    case 'trail-team':
      openTeamBuilder({
        title: 'Trail team', ids: lineIds(), max: CONFIG.teamSize, name: 'trail', key: String(S.trainer!.level), ok: mayFight, asFought: asFighter,
        onSave: (ids) => { T.ids = ids; rerender(); },
      });
      return true;
    case 'trail-hunt': T.ids = (S.trainer?.team ?? []).filter((x) => mayFight(S.creatures.get(x))).slice(0, CONFIG.teamSize); rerender(); return true;
    case 'trail-region': go(`#/trail/${id}`); return true;
    case 'trail-node': { const n = TRAIL_NODE[id]; if (n) go(`#/trail/${n.region}/${n.id}`); return true; }
    case 'trail-fight': {
      T.watch = false;
      // Your hunt team in its order, with rows once you set them here (or as your last Trail fight stood).
      const team = myTeam();
      const ids = team.map((c) => c.id);
      const pick = T.rows ?? (S.trainer?.trail?.last?.rows ? pickFrom(S.trainer.trail.last.team.map((c) => c.id), S.trainer.trail.last.rows) : null);
      const rows = rowsOut(ids, pick, myRows(team));
      send({ t: 'trailFight', node: id, ids, ...(rows ? { rows } : {}) });
      return true;
    }
    case 'trail-row': {
      const x = rowClick(id);
      if (!x) return true;
      const team = myTeam();
      T.rows = setRow(team.map(asFighter), Object.fromEntries(myRows(team)), x.id, x.row);
      rerender(); return true;
    }
    case 'trail-watch':
      if (boardOn()) { T.view = 'fight'; rerender(); const n = TRAIL_NODE[T.node]; const f = n?.kind === 'trainer' ? lastFight(n) : null; if (f) replayFromStart(f.result()); }
      else { T.watch = !T.watch; rerender(); }
      return true;
    case 'trail-board': T.view = 'board'; rerender(); return true;
  }
  return false;
}

/** A fight came back from the server: keep it for the node's result panel. */
export function onTrailFight(f: TrailFightDTO) {
  S.trailFight = f;
  T.watch = false;
  T.view = 'fight';
  rerender();
  replayFromStart(f.result);
}

/** The Help page's Trail entry. */
export function trailHelp(): string {
  return `<h4>The Trail</h4><p>Opened from the Map or the app bar at trainer Lv ${TRAIL_LEVEL}. Each region has its own path: trainers in order, each opening at a trainer level in the region's band, and the region's boss at the end. Trainers field set teams built round one idea (speed, a wall, a counter, a region's synergy). Fights are automatic and use your hunt team${boardOn() ? ' on the rows you set on the Trail page' : ' in its order'}, on the Arena's rules with held items; they never stop the hunt, and you can watch the last one again. The first win against each trainer pays its reward once; rematches pay nothing. The boss at the end opens once every trainer on its path is beaten, then fights as always (its key, its rules, the Boss Ladder): the first win against it pays one held item from its pool and ${CONFIG.trail.bossDiamonds} diamonds, once.${S.heldOn ? ' After that its wins and its Boss Ladder steps can drop pieces of its items (see Held items).' : ''}</p>`;
}

